import { UserProfile } from '@oy/types';
import update from 'immutability-helper';
import { AuthOtp, AuthUser } from 'src/models/Auth';
import { LoginData } from 'src/models/Login';
import { storage } from 'src/utilities/Storage';
import { ActionType, defaultAction } from '../types';

const auth = storage.get('auth', {});

export type AuthState = {
  isSessionExpired: boolean;
  isShowOtpModal: boolean;
  passwordExpired: boolean;
  session_id: string | null;
  token: string | null;
  user?: AuthUser;
  userDefault?: AuthUser;
  apiKeyStaging?: string | null;
  tokenStaging?: string | null;
  usernameStaging?: string | null;
  otp?: AuthOtp | null;
};

const initialState: AuthState = {
  isSessionExpired: false,
  isShowOtpModal: false,
  passwordExpired: false,
  session_id: auth.session_id,
  token: auth.token,
  user: auth.user,
  userDefault: auth.userDefault,
  apiKeyStaging: auth.apiKeyStaging,
  tokenStaging: auth.tokenStaging,
  otp: auth.otp,
};

const mapUserProfileToState = (data: UserProfile) => {
  return {
    company_id: data.company_id ?? '',
    company_username: data.company_username ?? '',
    email: data.email,
    full_name: data.full_name,
    roles: data.roles,
    username: data.username,
  };
};

const mapUserObject = (
  authObject: LoginData,
  profileObject: UserProfile,
): AuthState => {
  return {
    isSessionExpired: false,
    isShowOtpModal: false,
    passwordExpired: authObject.password_expired,
    session_id: authObject.session_id,
    token: authObject.token,
    user: {
      company_id: profileObject.company_id ?? '',
      company_username: profileObject.company_username,
      username: profileObject.username,
      full_name: profileObject.full_name,
      profile_pict: authObject.profile_pict,
      roles: profileObject.roles,
      payment_services: authObject.payment_services,
      wallet_info: authObject.wallet_info,
      email: profileObject.email,
    },
    otp: null,
  };
};

const authReducer = (
  state = initialState,
  action: ActionType = defaultAction,
): AuthState => {
  const { type, payload } = action;
  let newState: AuthState;

  switch (type) {
    case 'SET_EXPIRED_SESSION':
      newState = {
        ...state,
        isSessionExpired: true,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_SHOW_OTP_MODAL':
      return {
        ...state,
        isShowOtpModal: payload,
      };
    case 'SET_FULL_NAME':
      if (state.user) {
        newState = {
          ...state,
          user: {
            ...state.user,
            full_name: payload,
          },
        };
        storage.set('auth', newState);
        return newState;
      }
      return state;
    case 'SET_USERNAME_STAGING':
      newState = {
        ...state,
        usernameStaging: payload,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_TOKEN_STAGING':
      newState = {
        ...state,
        tokenStaging: payload,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_AUTH':
      newState = {
        ...state,
        ...mapUserObject(payload.authData, payload.userData),
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_SESSION_ID':
      newState = {
        ...state,
        session_id: payload,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_AUTH_DATA_STAGING':
      newState = update(state, {
        $merge: {
          session_id: payload.session_id,
          tokenStaging: payload.token,
        },
        user: {
          $merge: {
            payment_services: payload.payment_services,
            profile_pict: payload.profile_pict,
            wallet_info: payload.wallet_info,
          },
        },
      });
      storage.set('auth', newState);
      return newState;
    case 'PASSWORD_EXPIRED_CLEAR':
      newState = {
        ...state,
        passwordExpired: false,
      };
      storage.set('auth', newState);
      return newState;
    case 'CLEAR_AUTH':
      storage.set('auth', null);
      return initialState;
    case 'SET_API_KEY_STAGING':
      newState = {
        ...state,
        apiKeyStaging: payload,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_AUTH_USER':
      newState = update(state, {
        user: {
          $merge: {
            ...mapUserProfileToState(payload),
          },
        },
      });
      storage.set('auth', newState);
      return newState;
    case 'SET_DEFAULT_USER':
      newState = {
        ...state,
        userDefault: state.user,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_USER_TO_DEFAULT':
      newState = {
        ...state,
        user: state.userDefault,
      };
      storage.set('auth', newState);
      return newState;
    case 'SET_AUTH_OTP':
      newState = update(state, {
        otp: {
          $set: payload,
        },
      });
      storage.set('auth', newState);
      return newState;
    case 'CLEAR_AUTH_OTP':
      newState = update(state, {
        isShowOtpModal: {
          $set: false,
        },
        otp: {
          $set: null,
        },
      });
      storage.set('auth', newState);
      return newState;
    default:
      return state;
  }
};

export default authReducer;
